import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  FileText, CheckCircle2, Clock, PenLine, Users, PlusCircle, ArrowRight,
  Eye, UsersRound, TrendingUp, CalendarClock, Sparkles, Image as ImageIcon, Loader2, AlertCircle,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div data-testid={testid} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${color}`}><Icon size={20} /></div>
    <div className="text-3xl font-bold text-gray-900 font-serif-dev">{value}</div>
    <div className="text-sm text-gray-500 mt-1">{label}</div>
  </div>
);

const statusBadge = (s) => {
  const map = { published: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", draft: "bg-gray-100 text-gray-600" };
  const label = { published: "प्रकाशित", pending: "समीक्षा में", draft: "ड्राफ्ट" };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${map[s] || map.draft}`}>{label[s] || s}</span>;
};

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("hi-IN") : "—");

function TrafficPanel({ traffic }) {
  if (!traffic) {
    return (
      <div data-testid="traffic-loading" className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 text-gray-400 text-sm">
        ट्रैफ़िक डेटा लोड हो रहा है…
      </div>
    );
  }
  const maxView = Math.max(1, ...traffic.series.map((s) => s.views));

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5" data-testid="traffic-panel">
      {/* Left: 4 headline metrics + chart */}
      <div className="xl:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Eye size={17} className="text-[#D92525]" /> साइट ट्रैफ़िक</h2>
            <p className="text-xs text-gray-500 mt-0.5">पिछले 14 दिनों का view चार्ट · केवल एडमिन/एडिटर को दिखाई देता है</p>
          </div>
          <span className="text-[11px] uppercase tracking-wider text-gray-400">Anonymised</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div data-testid="traffic-today" className="rounded-lg bg-[#D92525]/6 border border-[#D92525]/15 p-3">
            <div className="text-[10px] uppercase tracking-wider text-[#D92525] font-semibold">आज</div>
            <div className="mt-1 text-2xl font-bold text-gray-900 font-serif-dev">{fmt(traffic.today_views)}</div>
            <div className="text-[11px] text-gray-500">{fmt(traffic.today_uniques)} अनोखे visitor</div>
          </div>
          <div data-testid="traffic-yesterday" className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">कल</div>
            <div className="mt-1 text-2xl font-bold text-gray-900 font-serif-dev">{fmt(traffic.yesterday_views)}</div>
            <div className="text-[11px] text-gray-500">views</div>
          </div>
          <div data-testid="traffic-week" className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">7 दिन</div>
            <div className="mt-1 text-2xl font-bold text-gray-900 font-serif-dev">{fmt(traffic.week_views)}</div>
            <div className="text-[11px] text-gray-500">{fmt(traffic.week_uniques)} अनोखे</div>
          </div>
          <div data-testid="traffic-total" className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">कुल</div>
            <div className="mt-1 text-2xl font-bold text-gray-900 font-serif-dev">{fmt(traffic.total_views)}</div>
            <div className="text-[11px] text-gray-500">{fmt(traffic.total_uniques)} अनोखे visitor</div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-gray-400 mb-2">
            <span className="flex items-center gap-1"><CalendarClock size={12} /> पिछले 14 दिन</span>
            <span className="flex items-center gap-1"><TrendingUp size={12} /> daily views</span>
          </div>
          <div className="flex items-end gap-1.5 h-32" data-testid="traffic-chart">
            {traffic.series.map((s) => {
              const h = Math.max(2, Math.round((s.views / maxView) * 100));
              return (
                <div key={s.date} className="flex-1 flex flex-col items-center group relative">
                  <div
                    style={{ height: `${h}%` }}
                    className="w-full rounded-t bg-[#0F0F0F] group-hover:bg-[#D92525] transition-colors"
                    title={`${s.label}: ${s.views} views · ${s.uniques} अनोखे`}
                  />
                  <div className="text-[9px] text-gray-400 mt-1">{s.date.slice(-2)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right: top articles */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-[#D92525]" /> टॉप 5 आर्टिकल
        </h3>
        {traffic.top_articles.length === 0 ? (
          <p className="text-xs text-gray-400">अभी कोई article view नहीं</p>
        ) : (
          <ol className="space-y-3" data-testid="traffic-top-articles">
            {traffic.top_articles.slice(0, 5).map((a, i) => (
              <li key={a.slug} className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-[#0F0F0F] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <Link to={`/news/${a.slug}`} target="_blank" className="block font-serif-dev font-semibold text-[14px] text-gray-900 hover:text-[#D92525] line-clamp-2">
                    {a.title}
                  </Link>
                  <div className="text-[11px] text-gray-500 mt-0.5">{a.kicker} · <b className="text-[#D92525]">{fmt(a.views)}</b> views</div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [traffic, setTraffic] = useState(null);
  const [missingImg, setMissingImg] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState("");
  const canSeeTraffic = user?.role === "admin" || user?.role === "editor";

  const loadMissing = () => {
    if (!canSeeTraffic) return;
    api.get("/stock-image/missing/count").then((r) => setMissingImg(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/articles").then((r) => setRecent(r.data.slice(0, 6))).catch(() => {});
    if (canSeeTraffic) {
      api.get("/stats/traffic").then((r) => setTraffic(r.data)).catch(() => {});
      loadMissing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeTraffic]);

  const runBulkImages = async (limit) => {
    setBulkBusy(true); setBulkMsg("");
    try {
      const { data } = await api.post("/stock-image/bulk", { limit });
      setBulkMsg(`✓ ${data.generated}/${data.attempted} cover images लग गईं${data.failed ? ` · ${data.failed} विफल` : ""}`);
      loadMissing();
    } catch (e) {
      setBulkMsg("Bulk image विफल — कुछ देर बाद कोशिश करें");
    } finally {
      setBulkBusy(false);
      setTimeout(() => setBulkMsg(""), 8000);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">नमस्ते, {user?.name}</h1>
          <p className="text-gray-500 text-sm mt-1">आज देनवा पोस्ट पर क्या चल रहा है, एक नज़र में।</p>
        </div>
        <Link to="/admin/articles/new" data-testid="dashboard-new-btn" className="inline-flex items-center gap-2 bg-[#D92525] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0A0F1E] transition-colors">
          <PlusCircle size={16} /> नई खबर लिखें
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
        <StatCard testid="stat-total" icon={FileText} label="कुल खबरें" value={stats?.total ?? "—"} color="bg-blue-50 text-blue-600" />
        <StatCard testid="stat-published" icon={CheckCircle2} label="प्रकाशित" value={stats?.published ?? "—"} color="bg-green-50 text-green-600" />
        <StatCard testid="stat-pending" icon={Clock} label="समीक्षा में" value={stats?.pending ?? "—"} color="bg-amber-50 text-amber-600" />
        <StatCard testid="stat-draft" icon={PenLine} label="ड्राफ्ट" value={stats?.draft ?? "—"} color="bg-gray-100 text-gray-600" />
        {user?.role === "admin" && <StatCard testid="stat-team" icon={Users} label="टीम सदस्य" value={stats?.team ?? "—"} color="bg-red-50 text-[#D92525]" />}
      </div>

      {canSeeTraffic && (
        <div className="mb-8">
          <TrafficPanel traffic={traffic} />
        </div>
      )}

      {canSeeTraffic && missingImg && missingImg.missing > 0 && (
        <div data-testid="ai-cover-widget" className="mb-8 bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-[#0F0F0F] text-white flex items-center justify-center shrink-0">
                <ImageIcon size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">Cover Image Filler <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">FREE</span></h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  <b className="text-[#D92525]">{missingImg.missing.toLocaleString("hi-IN")}</b> खबरें बिना असली photo के हैं
                  ({Math.round(missingImg.missing / (missingImg.total || 1) * 100)}%).
                  Category-मैच stock photo (Unsplash) एक-क्लिक में लग जाएगा। कोई LLM budget खर्च नहीं।
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => runBulkImages(5)}
                disabled={bulkBusy}
                data-testid="ai-bulk-5"
                className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />} अगली 5 भरो
              </button>
              <button
                onClick={() => runBulkImages(20)}
                disabled={bulkBusy}
                data-testid="ai-bulk-20"
                className="inline-flex items-center gap-1.5 bg-[#0F0F0F] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#D92525] disabled:opacity-60"
              >
                {bulkBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} अगली 20 भरो
              </button>
            </div>
          </div>
          {bulkMsg && (
            <div data-testid="ai-bulk-msg" className="mt-4 text-sm bg-[#F4F4F0] rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertCircle size={14} className="mt-0.5 text-[#D92525] shrink-0" /> {bulkMsg}
            </div>
          )}
          <p className="mt-4 text-[11px] text-gray-400">
            Unsplash Source (free, no API key) से category-themed 16:9 photos खींचते हैं। प्रति image ~1-3 सेकंड, बिल्कुल free।
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">हाल की खबरें</h2>
          <Link to="/admin/articles" className="text-sm text-[#D92525] font-medium inline-flex items-center gap-1">सभी देखें <ArrowRight size={14} /></Link>
        </div>
        <div className="divide-y divide-gray-50">
          {recent.length === 0 && <div className="px-6 py-8 text-center text-gray-400 text-sm">अभी कोई खबर नहीं</div>}
          {recent.map((a) => (
            <Link key={a.id} to={`/admin/articles/${a.id}/edit`} data-testid={`recent-${a.id}`} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50">
              <img src={a.thumbnail} alt="" className="w-14 h-11 rounded object-cover bg-gray-100 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-serif-dev font-semibold text-gray-900 truncate">{a.title}</div>
                <div className="text-xs text-gray-400 mt-0.5">{a.author_name} · {a.category}</div>
              </div>
              {statusBadge(a.status)}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
